import { Slot } from 'radix-ui';
import { isValidElement, type ReactElement, type ReactNode, useId } from 'react';
import { cn } from '@/shared/lib/cn';
import { Label } from '@/shared/ui/label';

type FieldProps = {
  label: ReactNode;
  /** Help text under the control. */
  description?: ReactNode;
  /** Validation message; rendered as an alert and marks the control invalid. */
  error?: string | undefined;
  /** Exactly one control (Input, Textarea, a `register()`-ed element…). */
  children: ReactElement<{ id?: string }>;
  className?: string;
};

/**
 * Label + control + help + error for plain `register()` inputs. Wires `id`, `aria-labelledby`, `aria-invalid` and
 * `aria-describedby` onto the child, so it also names a control that is not a form element (`RadioCards`).
 * For controlled controls use the `Form` wrapper instead.
 */
function Field({ label, description, error, children, className }: FieldProps) {
  const generatedId = useId();
  const id = (isValidElement(children) ? children.props.id : undefined) ?? generatedId;
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const describedBy = [description ? descriptionId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');
  return (
    <div data-slot="field" className={cn('grid gap-2', className)}>
      <Label id={labelId} htmlFor={id}>
        {label}
      </Label>
      <Slot.Root
        id={id}
        aria-labelledby={labelId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      >
        {children}
      </Slot.Root>
      {description ? (
        <p id={descriptionId} className="text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export { Field, type FieldProps };
